import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  Download,
  FileSpreadsheet,
  Info,
  LayoutGrid,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
  Stethoscope,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import InstantConsultationSectionsPanel from '../../components/MasterAdmin/vendor-catalog/InstantConsultationSectionsPanel';
import ConfirmDialog from '../../components/MasterAdmin/ui/ConfirmDialog';
import {
  VENDOR_FORM_CATALOG_TABS,
  INSTANT_CONSULTATION_SPECIALITY_TAB,
  addedByLabel,
  createVendorReferenceItem,
  deleteVendorReferenceItem,
  fetchVendorReferenceItemById,
  fetchVendorReferenceItems,
  fetchVendorReferencePendingSummary,
  isSpecialityItem,
  setVendorReferenceActive,
  setVendorReferenceApproval,
  updateVendorReferenceItem,
  downloadSpecialitiesCatalogExcel,
  downloadVendorReferenceCatalogExcel,
  uploadSpecialitiesExcel,
  uploadVendorReferenceCatalogExcel,
  type ApprovalStatus,
  type CatalogExcelImportResult,
  type CatalogFormPayload,
  type VendorFormCatalogType,
  type VendorReferenceItemDto,
  type VendorReferenceSpecialityDto,
  type VendorReferenceType,
} from '../../services/MasterAdmin/vendorReferenceDataService';
import {
  fetchInstantConsultationSections,
  type InstantConsultationSectionDto,
} from '../../services/MasterAdmin/instantConsultationSectionService';

export type VendorReferencePageVariant =
  | 'vendor-form-catalog'
  | 'instant-consultation-specialities';

interface VendorReferenceDataPageProps {
  variant?: VendorReferencePageVariant;
}

type ApiErrorPayload = {
  message?: string;
  debug?: {
    phase?: string;
    hint?: string;
    headersFound?: string[];
    headerScan?: unknown[];
    resolvedColumns?: Record<string, number | null>;
  };
};

const getApiErrorPayload = (e: unknown): ApiErrorPayload | null => {
  if (typeof e === 'object' && e !== null && 'response' in e) {
    const data = (e as { response?: { data?: ApiErrorPayload } }).response?.data;
    if (data && typeof data === 'object') return data;
  }
  return null;
};

const getApiErrorMessage = (e: unknown, fallback: string): string => {
  const data = getApiErrorPayload(e);
  if (data?.message) {
    const parts = [data.message];
    if (data.debug?.hint) parts.push(data.debug.hint);
    if (data.debug?.phase) parts.push(`(phase: ${data.debug.phase})`);
    if (data.debug?.headersFound?.length) {
      parts.push(`Headers found: ${data.debug.headersFound.join(', ')}`);
    }
    return parts.join(' ');
  }
  return fallback;
};

const EXCEL_IMPORT_HELP: Record<VendorReferenceType, string> = {
  languages:
    'Excel columns: name (required), sortOrder (optional). Duplicate names (case-insensitive) are skipped.',
  degrees:
    'Excel columns: name (required), doctorCategory, sortOrder (optional). Duplicate name + category pairs are skipped.',
  'medical-councils':
    'Excel columns: name (required), doctorCategory, sortOrder (optional). Duplicate name + category pairs are skipped.',
  specialities:
    'Excel columns: name, doctorCategory, description, sortOrder, instantConsultationFee, showInInstantConsultation, isFeatured, sectionTitle. Icons are left empty on import; upload images later per speciality if needed.',
};

const EXCEL_HELP_TOOLTIP_MAX_WIDTH = 384;
const EXCEL_HELP_VIEWPORT_MARGIN = 8;

const ExcelImportHelpIcon: React.FC<{ helpText: string }> = ({ helpText }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(
    null
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), 160);
  };

  const recomputePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const margin = EXCEL_HELP_VIEWPORT_MARGIN;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const width = Math.min(EXCEL_HELP_TOOLTIP_MAX_WIDTH, viewportW - margin * 2);
    const tipHeight = tooltipRef.current?.offsetHeight ?? 100;

    let top = rect.bottom + 6;
    if (top + tipHeight > viewportH - margin) {
      const above = rect.top - tipHeight - 6;
      top =
        above >= margin
          ? above
          : Math.max(margin, viewportH - tipHeight - margin);
    }

    let left = rect.right - width;
    if (left < margin) left = margin;
    if (left + width > viewportW - margin) {
      left = Math.max(margin, viewportW - width - margin);
    }

    setPos({ top, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    recomputePosition();
    const id = requestAnimationFrame(() => recomputePosition());
    return () => cancelAnimationFrame(id);
  }, [open, recomputePosition]);

  useEffect(() => {
    if (!open) return;

    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (tooltipRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleViewportChange = () => recomputePosition();

    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleEsc);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleEsc);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, recomputePosition]);

  useEffect(() => () => clearCloseTimer(), []);

  return (
    <div className="inline-flex shrink-0">
      <button
        ref={buttonRef}
        type="button"
        className="rounded p-0.5 text-emerald-700 hover:bg-emerald-100/80 dark:text-emerald-300 dark:hover:bg-emerald-900/40"
        aria-expanded={open}
        aria-label="Excel import column help"
        onMouseEnter={() => {
          clearCloseTimer();
          setOpen(true);
        }}
        onMouseLeave={scheduleClose}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <Info className="h-4 w-4" />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: pos.width,
              maxWidth: pos.width,
            }}
            className="z-[200] rounded-lg border border-gray-200 bg-white p-3 text-xs leading-relaxed text-gray-700 shadow-lg dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
          >
            {helpText}
          </div>,
          document.body
        )}
    </div>
  );
};

const cellTextClass = 'text-gray-900 dark:text-gray-100';

const normalizeCompareKey = (value: string): string => value.trim().toLowerCase();

const findDuplicateNameInRows = (
  rows: VendorReferenceItemDto[],
  name: string,
  excludeId?: string | null,
  doctorCategory?: string | null
): VendorReferenceItemDto | undefined =>
  rows.find((row) => {
    if (row.id === excludeId) return false;
    if (normalizeCompareKey(row.name) !== normalizeCompareKey(name)) return false;
    if (doctorCategory === undefined) return true;
    return (
      normalizeCompareKey(row.doctorCategory || '') ===
      normalizeCompareKey(doctorCategory || '')
    );
  });

const formatInstantConsultationFee = (row: VendorReferenceItemDto): string => {
  if (!isSpecialityItem(row) || row.instantConsultationFee == null || row.instantConsultationFee === '') {
    return '—';
  }
  const n = Number(row.instantConsultationFee);
  if (Number.isFinite(n)) {
    return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  }
  return `₹${row.instantConsultationFee}`;
};

const SpecialityIconThumb: React.FC<{ row: VendorReferenceSpecialityDto }> = ({ row }) => (
  <div
    className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-primary-50 dark:border-gray-600 dark:bg-primary-900/20"
    title={row.iconPath ? row.name : 'No icon uploaded'}
  >
    {row.iconPath ? (
      <img src={row.iconPath} alt="" className="h-full w-full object-contain p-0.5" />
    ) : (
      <Stethoscope className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden />
    )}
  </div>
);

const APPROVAL_BADGE: Record<ApprovalStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  APPROVED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  REJECTED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

type DrawerMode = 'closed' | 'view' | 'add' | 'edit';

interface FormState {
  name: string;
  doctorCategory: string;
  sortOrder: string;
  isActive: boolean;
  description: string;
  iconKey: string;
  instantConsultationFee: string;
  showInInstantConsultation: boolean;
  isFeatured: boolean;
  instantConsultationSectionId: string;
}

const emptyForm = (): FormState => ({
  name: '',
  doctorCategory: '',
  sortOrder: '0',
  isActive: true,
  description: '',
  iconKey: '',
  instantConsultationFee: '',
  showInInstantConsultation: false,
  isFeatured: false,
  instantConsultationSectionId: '',
});

const isVendorAddedRow = (row: VendorReferenceItemDto): boolean =>
  row.addedBy === 'VENDOR' ||
  (row as VendorReferenceItemDto & { source?: string }).source === 'VENDOR_OTHER';

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="grid grid-cols-[minmax(0,130px)_1fr] gap-3 border-b border-gray-100 py-2.5 text-sm last:border-0 dark:border-gray-700">
    <dt className="font-medium text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="min-w-0 break-words text-gray-900 dark:text-gray-100">{children}</dd>
  </div>
);

const MENU_WIDTH = 220;
const VIEWPORT_MARGIN = 8;

interface RowActionsMenuProps {
  row: VendorReferenceItemDto;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSetApproval: (status: ApprovalStatus) => void;
  onToggleActive: () => void;
}

const MenuItemBtn: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}> = ({ onClick, children, className = '' }) => (
  <button
    type="button"
    role="menuitem"
    className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700 ${className}`}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }}
  >
    {children}
  </button>
);

const RowActionsMenu: React.FC<RowActionsMenuProps> = ({
  row,
  open,
  onToggle,
  onClose,
  onView,
  onEdit,
  onDelete,
  onSetApproval,
  onToggleActive,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const showApprove = row.approvalStatus === 'PENDING';
  const showReject = isVendorAddedRow(row) && row.approvalStatus !== 'REJECTED';

  const run = useCallback(
    (action: () => void) => {
      onClose();
      action();
    },
    [onClose]
  );

  const recomputePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 260;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;

    let top = rect.bottom + 4;
    if (top + menuHeight > viewportH - VIEWPORT_MARGIN) {
      const above = rect.top - menuHeight - 4;
      top =
        above >= VIEWPORT_MARGIN
          ? above
          : Math.max(VIEWPORT_MARGIN, viewportH - menuHeight - VIEWPORT_MARGIN);
    }
    let left = rect.right - MENU_WIDTH;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (left + MENU_WIDTH > viewportW - VIEWPORT_MARGIN) {
      left = viewportW - MENU_WIDTH - VIEWPORT_MARGIN;
    }
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) recomputePosition();
    else setPos(null);
  }, [open, recomputePosition, showApprove, showReject]);

  useEffect(() => {
    if (!open) return;
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      onClose();
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleViewportChange = () => recomputePosition();
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleEsc);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleEsc);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, onClose, recomputePosition]);

  return (
    <div className="relative inline-block text-left">
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
      >
        <MoreVertical className="h-5 w-5" />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: MENU_WIDTH,
            }}
            className="z-[200] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-800"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <MenuItemBtn onClick={() => run(onView)}>
              <Eye className="h-4 w-4" /> View
            </MenuItemBtn>
            <MenuItemBtn onClick={() => run(onEdit)}>
              <Pencil className="h-4 w-4" /> Edit
            </MenuItemBtn>
            {(showApprove || showReject) && (
              <hr className="my-1 border-gray-100 dark:border-gray-700" />
            )}
            {showApprove && (
              <MenuItemBtn onClick={() => run(() => onSetApproval('APPROVED'))}>
                <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Approve
              </MenuItemBtn>
            )}
            {showReject && (
              <MenuItemBtn onClick={() => run(() => onSetApproval('REJECTED'))}>
                <XCircle className="h-4 w-4 text-red-500" /> Reject
              </MenuItemBtn>
            )}
            <hr className="my-1 border-gray-100 dark:border-gray-700" />
            <MenuItemBtn onClick={() => run(onToggleActive)}>
              {row.isActive ? 'Deactivate' : 'Activate'}
            </MenuItemBtn>
            <hr className="my-1 border-gray-100 dark:border-gray-700" />
            <MenuItemBtn
              onClick={() => run(onDelete)}
              className="text-red-600 dark:text-red-400"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </MenuItemBtn>
          </div>,
          document.body
        )}
    </div>
  );
};

const VendorReferenceDataPage: React.FC<VendorReferenceDataPageProps> = ({
  variant = 'vendor-form-catalog',
}) => {
  const isInstantConsultationSpecialities =
    variant === 'instant-consultation-specialities';
  const catalogTabs = isInstantConsultationSpecialities
    ? [INSTANT_CONSULTATION_SPECIALITY_TAB]
    : VENDOR_FORM_CATALOG_TABS;

  const [activeType, setActiveType] = useState<VendorReferenceType>(
    isInstantConsultationSpecialities ? 'specialities' : 'languages'
  );
  const [rows, setRows] = useState<VendorReferenceItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingCounts, setPendingCounts] = useState<Record<string, number>>({});

  const [filterApproval, setFilterApproval] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [filterAddedBy, setFilterAddedBy] = useState('');
  const [filterShownInInstant, setFilterShownInInstant] = useState('');
  const [search, setSearch] = useState('');

  const [drawerMode, setDrawerMode] = useState<DrawerMode>('closed');
  const [selected, setSelected] = useState<VendorReferenceItemDto | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<VendorReferenceItemDto | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [icSections, setIcSections] = useState<InstantConsultationSectionDto[]>([]);
  const [sectionsPanelOpen, setSectionsPanelOpen] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [importNotice, setImportNotice] = useState<string | null>(null);

  const isSpeciality = activeType === 'specialities';
  const activeTabMeta = useMemo(
    () => catalogTabs.find((t) => t.type === activeType) ?? catalogTabs[0]!,
    [activeType, catalogTabs]
  );

  const pageTitle = 'Vendor form catalog';

  const icSectionTitleById = useMemo(() => {
    const map = new Map<string, string>();
    icSections.forEach((s) => {
      if (s.id) map.set(s.id.toLowerCase(), s.title);
    });
    return map;
  }, [icSections]);

  const resolveInstantConsultationSectionTitle = (
    row: VendorReferenceSpecialityDto
  ): string | null => {
    const fromApi = row.sectionTitle?.trim();
    if (fromApi) return fromApi;

    const nestedTitle = row.instantConsultationSection?.title?.trim();
    if (nestedTitle) return nestedTitle;

    const sectionId = row.instantConsultationSectionId;
    if (sectionId) {
      return icSectionTitleById.get(sectionId.toLowerCase()) ?? null;
    }
    return null;
  };

  const instantConsultationSectionLabel = (
    row: VendorReferenceItemDto
  ): string => {
    if (!isSpecialityItem(row)) return '—';
    if (
      !isInstantConsultationSpecialities &&
      !row.showInInstantConsultation
    ) {
      return '—';
    }
    const title = resolveInstantConsultationSectionTitle(row);
    if (title) return title;
    if (row.instantConsultationSectionId) return 'Unknown section';
    return 'Unsectioned';
  };

  const loadIcSections = useCallback(async () => {
    try {
      const res = await fetchInstantConsultationSections();
      setIcSections(res.data || []);
    } catch {
      setIcSections([]);
    }
  }, []);

  useEffect(() => {
    if (isSpeciality || isInstantConsultationSpecialities) void loadIcSections();
  }, [isSpeciality, isInstantConsultationSpecialities, loadIcSections]);

  const loadPending = useCallback(async () => {
    try {
      const res = await fetchVendorReferencePendingSummary();
      setPendingCounts(res.data || {});
    } catch {
      /* ignore */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Parameters<typeof fetchVendorReferenceItems>[1] = {
        limit: 200,
        page: 1,
      };
      if (filterApproval) params.approvalStatus = filterApproval as ApprovalStatus;
      if (filterActive === 'true' || filterActive === 'false') {
        params.isActive = filterActive === 'true';
      }
      if (filterAddedBy) params.addedBy = filterAddedBy as 'MASTER_ADMIN' | 'VENDOR';
      if (
        isInstantConsultationSpecialities &&
        (filterShownInInstant === 'true' || filterShownInInstant === 'false')
      ) {
        params.shownInInstant = filterShownInInstant === 'true';
      }
      if (search.trim()) params.search = search.trim();
      const response = await fetchVendorReferenceItems(activeType, params);
      setRows(response.data || []);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Failed to load catalog items.'));
    } finally {
      setLoading(false);
    }
  }, [
    activeType,
    filterApproval,
    filterActive,
    filterAddedBy,
    filterShownInInstant,
    isInstantConsultationSpecialities,
    search,
  ]);

  useEffect(() => {
    void load();
    void loadPending();
  }, [load, loadPending]);

  const closeDrawer = () => {
    setDrawerMode('closed');
    setSelected(null);
    setIconFile(null);
    setIconPreview(null);
  };

  const rowToForm = (row: VendorReferenceItemDto): FormState => {
    const base = emptyForm();
    base.name = row.name;
    base.doctorCategory = row.doctorCategory || '';
    base.sortOrder = String(row.sortOrder ?? 0);
    base.isActive = row.isActive;
    if (isSpecialityItem(row)) {
      base.description = row.description || '';
      base.iconKey = row.iconKey || '';
      base.instantConsultationFee =
        row.instantConsultationFee != null ? String(row.instantConsultationFee) : '';
      base.showInInstantConsultation = row.showInInstantConsultation;
      base.isFeatured = row.isFeatured;
      base.instantConsultationSectionId = row.instantConsultationSectionId || '';
    }
    return base;
  };

  const openView = async (row: VendorReferenceItemDto) => {
    setOpenMenuId(null);
    setDrawerMode('view');
    setSelected(row);
    setDrawerLoading(true);
    try {
      const res = await fetchVendorReferenceItemById(activeType, row.id);
      if (res.data) setSelected(res.data);
    } catch {
      /* keep list row */
    } finally {
      setDrawerLoading(false);
    }
  };

  const openAdd = () => {
    setOpenMenuId(null);
    setSelected(null);
    const next = emptyForm();
    if (isInstantConsultationSpecialities) {
      next.showInInstantConsultation = true;
    }
    setForm(next);
    setIconFile(null);
    setIconPreview(null);
    setDrawerMode('add');
  };

  const openEdit = (row: VendorReferenceItemDto) => {
    setOpenMenuId(null);
    setSelected(row);
    setForm(rowToForm(row));
    setIconFile(null);
    setIconPreview(isSpecialityItem(row) ? row.iconPath || null : null);
    setDrawerMode('edit');
  };

  const buildPayload = (): CatalogFormPayload => {
    const payload: CatalogFormPayload = {
      name: form.name.trim(),
      doctorCategory: form.doctorCategory.trim() || null,
      sortOrder: parseInt(form.sortOrder, 10) || 0,
      isActive: form.isActive,
    };
    if (isSpeciality) {
      Object.assign(payload, {
        description: form.description.trim() || null,
        iconKey: form.iconKey.trim() || null,
        instantConsultationFee: form.instantConsultationFee.trim() || null,
        showInInstantConsultation: form.showInInstantConsultation,
        isFeatured: form.isFeatured,
        instantConsultationSectionId:
          form.showInInstantConsultation || isInstantConsultationSpecialities
            ? form.instantConsultationSectionId || null
            : null,
      });
    }
    return payload;
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (isSpeciality) {
      const excludeId = drawerMode === 'edit' && selected ? selected.id : null;
      const duplicate = findDuplicateNameInRows(rows, form.name, excludeId);
      if (duplicate) {
        setError(`A speciality named "${form.name.trim()}" already exists.`);
        return;
      }
    } else if (activeType === 'languages') {
      const excludeId = drawerMode === 'edit' && selected ? selected.id : null;
      const duplicate = findDuplicateNameInRows(rows, form.name, excludeId);
      if (duplicate) {
        setError(`A language named "${form.name.trim()}" already exists.`);
        return;
      }
    } else if (activeType === 'degrees' || activeType === 'medical-councils') {
      const excludeId = drawerMode === 'edit' && selected ? selected.id : null;
      const duplicate = findDuplicateNameInRows(
        rows,
        form.name,
        excludeId,
        form.doctorCategory
      );
      if (duplicate) {
        setError(
          `An item named "${form.name.trim()}" with this doctor category already exists.`
        );
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      if (drawerMode === 'edit' && selected) {
        await updateVendorReferenceItem(activeType, selected.id, payload, iconFile);
      } else {
        await createVendorReferenceItem(activeType, payload, iconFile);
      }
      closeDrawer();
      await load();
      await loadPending();
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  const handleApproval = async (row: VendorReferenceItemDto, status: ApprovalStatus) => {
    setOpenMenuId(null);
    setError(null);
    try {
      await setVendorReferenceApproval(activeType, row.id, {
        approvalStatus: status,
        isActive: status === 'APPROVED',
      });
      await load();
      await loadPending();
      if (selected?.id === row.id && drawerMode === 'view') {
        const res = await fetchVendorReferenceItemById(activeType, row.id);
        if (res.data) setSelected(res.data);
      }
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Status update failed.'));
    }
  };

  const handleToggleActive = async (row: VendorReferenceItemDto) => {
    setOpenMenuId(null);
    setError(null);
    try {
      await setVendorReferenceActive(activeType, row.id, !row.isActive);
      await load();
      if (selected?.id === row.id) {
        const res = await fetchVendorReferenceItemById(activeType, row.id);
        if (res.data) setSelected(res.data);
      }
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Active status update failed.'));
    }
  };

  const requestDelete = (row: VendorReferenceItemDto) => {
    setOpenMenuId(null);
    setDeleteTarget(row);
  };

  const cancelDelete = () => {
    if (!deleteBusy) setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setError(null);
    try {
      await deleteVendorReferenceItem(activeType, deleteTarget.id);
      if (selected?.id === deleteTarget.id) closeDrawer();
      setDeleteTarget(null);
      await load();
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Delete failed.'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const onIconPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const applyExcelImportSummary = (summary: CatalogExcelImportResult, label: string) => {
    const parts = [
      summary.createdCount ? `${summary.createdCount} created` : '',
      summary.updatedCount ? `${summary.updatedCount} updated` : '',
      summary.skippedCount ? `${summary.skippedCount} skipped` : '',
      summary.errorCount ? `${summary.errorCount} failed` : '',
    ].filter(Boolean);

    const scanHint =
      summary.rowsWithName != null && summary.meta?.lastRowScanned != null
        ? ` (${summary.rowsWithName} named row(s) in rows ${
            (summary.meta.headerRowNumber ?? 0) + 1
          }–${summary.meta.lastRowScanned})`
        : '';

    if (summary.warning) {
      setError(`${summary.warning}${scanHint}`);
    } else if (summary.errorCount > 0 && summary.errors[0]) {
      setError(
        `Import finished: ${parts.join(', ')}${scanHint}. First error (row ${summary.errors[0].row}): ${summary.errors[0].message}`
      );
    } else if (
      summary.createdCount === 0 &&
      !summary.updatedCount &&
      summary.skippedCount > 0
    ) {
      setError(`Import finished: ${parts.join(', ')}${scanHint}. No new rows were added.`);
    } else if (summary.createdCount === 0 && !summary.updatedCount) {
      setError(
        `Import finished: ${parts.join(', ') || '0 created'}${scanHint}. No ${label} were added. Check the file has a "name" column and data rows below the header.`
      );
    } else if (summary.createdCount > 0 || (summary.updatedCount ?? 0) > 0) {
      setImportNotice(`Import finished: ${parts.join(', ')}${scanHint}.`);
    } else {
      setError(
        `Import finished: ${parts.join(', ') || 'no changes'}${scanHint}. No ${label} were added or updated.`
      );
    }
  };

  const handleExcelDownload = async () => {
    setDownloadingExcel(true);
    setError(null);
    try {
      const label = activeTabMeta.label;
      const result = isSpeciality
        ? await downloadSpecialitiesCatalogExcel()
        : await downloadVendorReferenceCatalogExcel(activeType as VendorFormCatalogType);
      setImportNotice(
        `Downloaded ${result.rowCount} ${label.toLowerCase()} row(s) as ${result.fileName}. Import columns match the upload template; extra columns (approval, id) are ignored on import.`
      );
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Excel download failed.'));
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleExcelUpload: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingExcel(true);
    setError(null);
    setImportNotice(null);
    try {
      const res = isInstantConsultationSpecialities
        ? await uploadSpecialitiesExcel(file)
        : activeType === 'specialities'
          ? null
          : await uploadVendorReferenceCatalogExcel(activeType, file);

      if (!res) {
        setError('Excel import is not available for this catalog tab.');
        return;
      }

      const summary = res.data;
      const label = activeTabMeta.label.toLowerCase();

      if (process.env.NODE_ENV === 'development') {
        console.log(`${activeType} Excel import result:`, summary, res.debug);
      }

      applyExcelImportSummary(summary, label);

      await load();
      await loadPending();
    } catch (e: unknown) {
      const payload = getApiErrorPayload(e);
      if (payload?.debug) {
        console.error(`${activeType} Excel import debug:`, payload.debug);
      }
      setError(getApiErrorMessage(e, 'Excel import failed.'));
    } finally {
      setUploadingExcel(false);
    }
  };

  const drawerTitle =
    drawerMode === 'view'
      ? 'View details'
      : drawerMode === 'edit'
        ? `Edit ${activeTabMeta.label.slice(0, -1)}`
        : drawerMode === 'add'
          ? `Add ${activeTabMeta.label.slice(0, -1)}`
          : '';

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto">
      {!isInstantConsultationSpecialities && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{pageTitle}</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {activeTabMeta.description}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            When doctors choose &quot;Other&quot; during registration or profile update, new values are
            added here as <span className="font-semibold text-amber-700 dark:text-amber-300">Pending</span>{' '}
            (not auto-approved). Use row actions → Approve or Reject.
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            Specialities and instant consultation sections are under Operations.
          </p>
        </div>
      )}

      {isInstantConsultationSpecialities && (
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Vendor-submitted specialities appear as{' '}
          <span className="font-semibold text-amber-700 dark:text-amber-300">Pending</span> until you
          approve them from the row menu.
        </p>
      )}

      {importNotice && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{importNotice}</span>
          <button
            type="button"
            className="ml-auto"
            onClick={() => setImportNotice(null)}
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
          <button type="button" className="ml-auto" onClick={() => setError(null)} aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {!isInstantConsultationSpecialities && catalogTabs.length > 1 && (
      <div className="mb-4 flex overflow-x-auto whitespace-nowrap max-w-full gap-2 border-b border-gray-200 dark:border-gray-700 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {catalogTabs.map((tab) => {
          const pending = pendingCounts[tab.type] || 0;
          const active = activeType === tab.type;
          return (
            <button
              key={tab.type}
              type="button"
              onClick={() => {
                setActiveType(tab.type);
                closeDrawer();
              }}
              className={`relative px-4 py-2.5 text-sm font-semibold transition-colors shrink-0 ${
                active
                  ? 'border-b-2 border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400'
              }`}
            >
              {tab.label}
              {pending > 0 && (
                <span className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                  {pending}
                </span>
              )}
            </button>
          );
        })}
      </div>
      )}

      <>
      {isInstantConsultationSpecialities && (
        <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setSectionsPanelOpen(true)}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            <LayoutGrid className="h-4 w-4" />
            Manage sections
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="min-w-[200px] flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        />
        <select
          value={filterApproval}
          onChange={(e) => setFilterApproval(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">All statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        {(pendingCounts[activeType] || 0) > 0 && filterApproval !== 'PENDING' && (
          <button
            type="button"
            onClick={() => setFilterApproval('PENDING')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200 dark:hover:bg-amber-900/50"
          >
            <AlertTriangle className="h-4 w-4" />
            Pending approvals ({pendingCounts[activeType]})
          </button>
        )}
        <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap max-w-full [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden shrink-0">
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 shrink-0"
          >
            <option value="">Active + inactive</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          {!isInstantConsultationSpecialities && (
            <select
              value={filterAddedBy}
              onChange={(e) => setFilterAddedBy(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 shrink-0"
            >
              <option value="">All creators</option>
              <option value="MASTER_ADMIN">Master Admin</option>
              <option value="VENDOR">Vendor</option>
            </select>
          )}
          {isInstantConsultationSpecialities && (
            <select
              value={filterShownInInstant}
              onChange={(e) => setFilterShownInInstant(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 shrink-0"
            >
              <option value="">All instant visibility</option>
              <option value="true">Shown in instant</option>
              <option value="false">Hidden from instant</option>
            </select>
          )}
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 shrink-0"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
        <div className="inline-flex flex-wrap items-center gap-1">
          <button
            type="button"
            disabled={downloadingExcel || uploadingExcel}
            onClick={() => void handleExcelDownload()}
            className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-60 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200 dark:hover:bg-blue-900/40"
          >
            {downloadingExcel ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {downloadingExcel ? 'Downloading…' : 'Download Excel'}
          </button>
          <label
            className={`inline-flex cursor-pointer items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200 ${
              uploadingExcel || downloadingExcel ? 'pointer-events-none opacity-60' : ''
            }`}
          >
            <input
              type="file"
              accept=".xlsx,.xls,.csv,text/csv"
              className="hidden"
              disabled={uploadingExcel || downloadingExcel}
              onChange={handleExcelUpload}
            />
            {uploadingExcel ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4" />
            )}
            {uploadingExcel ? 'Uploading…' : 'Upload Excel'}
          </label>
          <ExcelImportHelpIcon helpText={EXCEL_IMPORT_HELP[activeType]} />
        </div>
        {!isInstantConsultationSpecialities && (
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {loading ? (
          <div className="flex justify-center gap-2 py-16 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-500">No items found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  {isInstantConsultationSpecialities && (
                    <th className="w-14 px-3 py-3 text-center font-semibold text-gray-900 dark:text-gray-100">
                      Sr. No.
                    </th>
                  )}
                  {isInstantConsultationSpecialities && (
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                      Icon
                    </th>
                  )}
                  <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                    Name
                  </th>
                  {isInstantConsultationSpecialities && (
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                      Price
                    </th>
                  )}
                  {isInstantConsultationSpecialities && (
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                      Section name
                    </th>
                  )}
                  {isInstantConsultationSpecialities && (
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                      Shown in instant
                    </th>
                  )}
                  {isSpeciality && !isInstantConsultationSpecialities && (
                    <>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                        Instant
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                        IC section
                      </th>
                    </>
                  )}
                  {!isInstantConsultationSpecialities &&
                    (activeType === 'degrees' || activeType === 'medical-councils') && (
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                        Category
                      </th>
                    )}
                  {!isInstantConsultationSpecialities && (
                    <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                      Added by
                    </th>
                  )}
                  <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                    Approval
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                    Active
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((row, index) => (
                  <tr key={row.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/30">
                    {isInstantConsultationSpecialities && (
                      <td className="px-3 py-3 text-center tabular-nums text-gray-600 dark:text-gray-400">
                        {index + 1}
                      </td>
                    )}
                    {isInstantConsultationSpecialities && (
                      <td className="px-4 py-3">
                        {isSpecialityItem(row) ? (
                          <SpecialityIconThumb row={row} />
                        ) : (
                          '—'
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void openView(row)}
                        className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                      >
                        {row.name}
                      </button>
                      {row.slug && !isInstantConsultationSpecialities && (
                        <div className="text-xs text-gray-600 dark:text-gray-300">{row.slug}</div>
                      )}
                    </td>
                    {isInstantConsultationSpecialities && (
                      <td className={`px-4 py-3 font-medium tabular-nums ${cellTextClass}`}>
                        {formatInstantConsultationFee(row)}
                      </td>
                    )}
                    {isInstantConsultationSpecialities && (
                      <td className={`px-4 py-3 font-medium ${cellTextClass}`}>
                        {instantConsultationSectionLabel(row)}
                      </td>
                    )}
                    {isInstantConsultationSpecialities && (
                      <td className={`px-4 py-3 ${cellTextClass}`}>
                        {isSpecialityItem(row) && row.presentInDoctorClinic ? (
                          <span className="font-medium text-emerald-700 dark:text-emerald-400">Yes</span>
                        ) : (
                          <span className="font-medium text-amber-700 dark:text-amber-400">No</span>
                        )}
                      </td>
                    )}
                    {isSpeciality && !isInstantConsultationSpecialities && (
                      <>
                        <td className={`px-4 py-3 ${cellTextClass}`}>
                          {isSpecialityItem(row) && row.showInInstantConsultation ? (
                            <span className="font-medium text-emerald-700 dark:text-emerald-400">On</span>
                          ) : (
                            <span className="font-medium text-amber-700 dark:text-amber-400">Off</span>
                          )}
                        </td>
                        <td className={`px-4 py-3 ${cellTextClass}`}>
                          {instantConsultationSectionLabel(row)}
                        </td>
                      </>
                    )}
                    {!isInstantConsultationSpecialities &&
                      (activeType === 'degrees' || activeType === 'medical-councils') && (
                        <td className={`px-4 py-3 ${cellTextClass}`}>
                          {row.doctorCategory || '—'}
                        </td>
                      )}
                    {!isInstantConsultationSpecialities && (
                    <td className={`px-4 py-3 font-medium ${cellTextClass}`}>
                      {addedByLabel(row.addedBy ?? 'MASTER_ADMIN')}
                      {row.addedById && (
                        <div className="mt-0.5 font-mono text-[10px] text-gray-600 dark:text-gray-300 truncate max-w-[140px]">
                          {row.addedById}
                        </div>
                      )}
                    </td>
                    )}
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${APPROVAL_BADGE[row.approvalStatus]}`}
                      >
                        {row.approvalStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {row.isActive ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 dark:text-red-400" />
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <RowActionsMenu
                        row={row}
                        open={openMenuId === row.id}
                        onToggle={() =>
                          setOpenMenuId((p) => (p === row.id ? null : row.id))
                        }
                        onClose={() => setOpenMenuId(null)}
                        onView={() => void openView(row)}
                        onEdit={() => openEdit(row)}
                        onDelete={() => requestDelete(row)}
                        onSetApproval={(s) => void handleApproval(row, s)}
                        onToggleActive={() => void handleToggleActive(row)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>

      {drawerMode !== 'closed' && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close panel"
            onClick={closeDrawer}
          />
          <aside className="relative flex h-full w-full max-w-lg flex-col bg-white shadow-2xl dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">{drawerTitle}</h2>
                {selected && drawerMode === 'view' && (
                  <p className="text-sm text-gray-500">{selected.name}</p>
                )}
              </div>
              <button type="button" onClick={closeDrawer} aria-label="Close">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {drawerLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : drawerMode === 'view' && selected ? (
                <dl>
                  <DetailRow label="ID">
                    <span className="font-mono text-xs">{selected.id}</span>
                  </DetailRow>
                  <DetailRow label="Name">{selected.name}</DetailRow>
                  <DetailRow label="Slug">{selected.slug || '—'}</DetailRow>
                  {(isSpeciality ||
                    activeType === 'degrees' ||
                    activeType === 'medical-councils') && (
                    <DetailRow label="Doctor category">
                      {selected.doctorCategory || '—'}
                    </DetailRow>
                  )}
                  {!isInstantConsultationSpecialities && (
                    <DetailRow label="Added by">
                      {addedByLabel(selected.addedBy ?? 'MASTER_ADMIN')}
                    </DetailRow>
                  )}
                  {!isInstantConsultationSpecialities && (
                    <DetailRow label="Added by ID">
                      {selected.addedById ? (
                        <span className="font-mono text-xs break-all">{selected.addedById}</span>
                      ) : (
                        '—'
                      )}
                    </DetailRow>
                  )}
                  <DetailRow label="Approval">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${APPROVAL_BADGE[selected.approvalStatus]}`}
                    >
                      {selected.approvalStatus}
                    </span>
                  </DetailRow>
                  <DetailRow label="Active">{selected.isActive ? 'Yes' : 'No'}</DetailRow>
                  <DetailRow label="Sort order">{selected.sortOrder}</DetailRow>
                  {selected.reviewedByAdminId && (
                    <DetailRow label="Reviewed by admin">
                      <span className="font-mono text-xs">{selected.reviewedByAdminId}</span>
                    </DetailRow>
                  )}
                  <DetailRow label="Reviewed at">{formatDateTime(selected.reviewedAt)}</DetailRow>
                  {isSpecialityItem(selected) && (
                    <>
                      <DetailRow label="Description">{selected.description || '—'}</DetailRow>
                      <DetailRow label="Icon key">{selected.iconKey || '—'}</DetailRow>
                      <DetailRow label="Icon">
                        {selected.iconPath ? (
                          <img
                            src={selected.iconPath}
                            alt=""
                            className="h-14 w-14 rounded-lg border object-contain"
                          />
                        ) : (
                          '—'
                        )}
                      </DetailRow>
                      <DetailRow label="Consult fee">
                        {selected.instantConsultationFee != null
                          ? `₹${selected.instantConsultationFee}`
                          : '—'}
                      </DetailRow>
                      <DetailRow label="Instant consult">
                        {selected.showInInstantConsultation ? 'Shown' : 'Hidden'}
                      </DetailRow>
                      <DetailRow label="Featured">
                        {selected.isFeatured ? 'Yes' : 'No'}
                      </DetailRow>
                      <DetailRow
                        label={
                          isInstantConsultationSpecialities ? 'Section name' : 'IC section'
                        }
                      >
                        {instantConsultationSectionLabel(selected)}
                      </DetailRow>
                    </>
                  )}
                  <DetailRow label="Created">{formatDateTime(selected.createdAt)}</DetailRow>
                  <DetailRow label="Updated">{formatDateTime(selected.updatedAt)}</DetailRow>
                </dl>
              ) : (
                <div className="space-y-4">
                  <label className="block text-sm">
                    <span className="font-medium">Name *</span>
                    <input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
                    />
                  </label>
                  {(isSpeciality ||
                    activeType === 'degrees' ||
                    activeType === 'medical-councils') && (
                    <label className="block text-sm">
                      <span className="font-medium">Doctor category</span>
                      <input
                        value={form.doctorCategory}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, doctorCategory: e.target.value }))
                        }
                        placeholder="e.g. Allopathy, Dental"
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
                      />
                    </label>
                  )}
                  <label className="block text-sm">
                    <span className="font-medium">Sort order</span>
                    <input
                      type="number"
                      value={form.sortOrder}
                      onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                    />
                    Active
                  </label>
                  {isSpeciality && (
                    <>
                      <label className="block text-sm">
                        <span className="font-medium">Description</span>
                        <textarea
                          value={form.description}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, description: e.target.value }))
                          }
                          rows={2}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
                        />
                      </label>
                      <label className="block text-sm">
                        <span className="font-medium">Icon key (optional preset)</span>
                        <input
                          value={form.iconKey}
                          onChange={(e) => setForm((f) => ({ ...f, iconKey: e.target.value }))}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
                        />
                      </label>
                      <label className="block text-sm">
                        <span className="font-medium">Upload icon</span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          onChange={onIconPick}
                          className="mt-1 block w-full text-sm"
                        />
                        {iconPreview && (
                          <img
                            src={iconPreview}
                            alt=""
                            className="mt-2 h-16 w-16 rounded-lg border object-contain"
                          />
                        )}
                      </label>
                      <label className="block text-sm">
                        <span className="font-medium">Instant consultation fee (₹)</span>
                        <input
                          type="number"
                          value={form.instantConsultationFee}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, instantConsultationFee: e.target.value }))
                          }
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
                        />
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.showInInstantConsultation}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              showInInstantConsultation: e.target.checked,
                              instantConsultationSectionId: e.target.checked
                                ? f.instantConsultationSectionId
                                : '',
                            }))
                          }
                        />
                        Show in instant consultation
                      </label>
                      {(form.showInInstantConsultation ||
                        isInstantConsultationSpecialities) && (
                        <label className="block text-sm">
                          <span className="font-medium">Instant consultation section</span>
                          <select
                            value={form.instantConsultationSectionId}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                instantConsultationSectionId: e.target.value,
                              }))
                            }
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
                          >
                            <option value="">Unsectioned (no group)</option>
                            {icSections.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.title}
                                {!s.isActive ? ' (inactive)' : ''}
                              </option>
                            ))}
                          </select>
                          {icSections.length === 0 && (
                            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                              Use &quot;Manage sections&quot; on this page to add a section,
                              then assign it here.
                            </p>
                          )}
                        </label>
                      )}
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.isFeatured}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, isFeatured: e.target.checked }))
                          }
                        />
                        Featured in top carousel
                      </label>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
              <button
                type="button"
                onClick={closeDrawer}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-gray-600"
              >
                Close
              </button>
              {drawerMode === 'view' && selected && (
                <button
                  type="button"
                  onClick={() => openEdit(selected)}
                  className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white"
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </button>
              )}
              {(drawerMode === 'add' || drawerMode === 'edit') && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void handleSave()}
                  className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save
                </button>
              )}
            </div>
          </aside>
        </div>
      )}

      {sectionsPanelOpen && (
        <div className="fixed inset-0 z-[55] flex justify-end">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close sections panel"
            onClick={() => setSectionsPanelOpen(false)}
          />
          <aside className="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Instant consultation sections
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Group specialities on the instant consultation screen (e.g. Primary Care).
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSectionsPanelOpen(false)}
                aria-label="Close"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <InstantConsultationSectionsPanel
                hideHeader
                onSectionsChange={() => void loadIcSections()}
              />
            </div>
          </aside>
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget != null}
        title="Delete catalogue item?"
        message={
          deleteTarget
            ? `Remove "${deleteTarget.name}" from the catalog? This cannot be undone.`
            : ''
        }
        tone="danger"
        confirmLabel="Delete"
        busy={deleteBusy}
        onCancel={cancelDelete}
        onConfirm={() => void confirmDelete()}
      />

    </div>
  );
};

export default VendorReferenceDataPage;
